import { readOnlyAnnouncementID } from "#skyward_global";
import { ModalLayer, newModalLayer } from "#skyward_modalLayer";
import debounce from "./skyward.ui.debounce";
import { registerEvents } from "./skyward.ui.events";
import { getSvg } from "./skyward.ui.svg";
import { JQueryInput } from "./types";
import { Key } from "./types/skyward/svg";

const skyward = window.skyward;

const DEFAULT_SETTINGS =
{
    color: "",
    preferredFormat: "hex" as Spectrum.ColorFormatName,
    theme: "sp-skyward ui-control-container",
    palette: [
        ["rgb(0, 0, 0)", "rgb(67, 67, 67)", "rgb(102, 102, 102)", "rgb(153, 153, 153)", "rgb(183, 183, 183)",
            "rgb(204, 204, 204)", "rgb(217, 217, 217)", "rgb(239, 239, 239)", "rgb(243, 243, 243)", "rgb(255, 255, 255)"],
        ["rgb(152, 0, 0)", "rgb(255, 0, 0)", "rgb(255, 153, 0)", "rgb(255, 255, 0)", "rgb(0, 255, 0)",
            "rgb(0, 255, 255)", "rgb(74, 134, 232)", "rgb(0, 0, 255)", "rgb(153, 0, 255)", "rgb(255, 0, 255)"],
        ["rgb(230, 184, 175)", "rgb(244, 204, 204)", "rgb(252, 229, 205)", "rgb(255, 242, 204)", "rgb(217, 234, 211)",
            "rgb(208, 224, 227)", "rgb(201, 218, 248)", "rgb(207, 226, 243)", "rgb(217, 210, 233)", "rgb(234, 209, 220)"],
        ["rgb(221, 126, 107)", "rgb(234, 153, 153)", "rgb(249, 203, 156)", "rgb(255, 229, 153)", "rgb(182, 215, 168)",
            "rgb(162, 196, 201)", "rgb(164, 194, 244)", "rgb(159, 197, 232)", "rgb(180, 167, 214)", "rgb(213, 166, 189)"],
        ["rgb(204, 65, 37)", "rgb(224, 102, 102)", "rgb(246, 178, 107)", "rgb(255, 217, 102)", "rgb(147, 196, 125)",
            "rgb(118, 165, 175)", "rgb(109, 158, 235)", "rgb(111, 168, 220)", "rgb(142, 124, 195)", "rgb(194, 123, 160)"],
        ["rgb(166, 28, 0)", "rgb(204, 0, 0)", "rgb(230, 145, 56)", "rgb(241, 194, 50)", "rgb(106, 168, 79)",
            "rgb(69, 129, 142)", "rgb(60, 120, 216)", "rgb(61, 133, 198)", "rgb(103, 78, 167)", "rgb(166, 77, 121)"],
        ["rgb(133, 32, 12)", "rgb(153, 0, 0)", "rgb(180, 95, 6)", "rgb(191, 144, 0)", "rgb(56, 118, 29)",
            "rgb(19, 79, 92)", "rgb(17, 85, 204)", "rgb(11, 83, 148)", "rgb(53, 28, 117)", "rgb(116, 27, 71)"],
        ["rgb(91, 15, 0)", "rgb(102, 0, 0)", "rgb(120, 63, 4)", "rgb(127, 96, 0)", "rgb(39, 78, 19)",
            "rgb(12, 52, 61)", "rgb(28, 69, 135)", "rgb(7, 55, 99)", "rgb(32, 18, 77)", "rgb(76, 17, 48)"]
    ]
};

const DEFAULT_INPUT_AND_PALETTE_SETTINGS = $.extend({}, DEFAULT_SETTINGS,
    {
        showPalette: true,
        showInput: true,
        palette:
            [
                ["#FFFFCC", "#FFFF99", "#CCCC99", "#CCFFCC", "#99FF99"],
                ["#99CC99", "#CCFFFF", "#99FFFF", "#99CCCC", "#CCCCFF"],
                ["#9999FF", "#9999CC", "#FFCCFF", "#FF99FF", "#CC99CC"],
                ["#FFCCCC", "#FF9999", "#CC9999", "#FFFFFF", "#CCCCCC"]
            ],
        chooseText: "Select"
    });

const DEFAULT_PARTIAL_PALETTE_ONLY_SETTINGS = $.extend({}, DEFAULT_SETTINGS,
    {
        showPaletteOnly: true,
        hideAfterPaletteSelect: true,
        palette:
            [
                ["#CCFF00", "#FF9966", "#FF99CC", "#5575B4", "#66CCCC"],
                ["#CCCCFF", "#A30000", "#009999", "#5BCF57", "#FFCC33"]
            ],
        chooseText: "Select"
    });

const DEFAULT_PALETTE_ONLY_SETTINGS = $.extend({}, DEFAULT_SETTINGS,
    {
        showPaletteOnly: true,
        hideAfterPaletteSelect: true,
        palette: [["#FFFFFF", "#000000"]]
    });

const DEFAULT_TEACHER_INPUT_PALETTE_SETTINGS = $.extend({}, DEFAULT_SETTINGS,
    {
        showPalette: true,
        showInput: true,
        palette:
            [
                ["#aafaa8", "#f4fe8f", "#fc9e6e", "#faaaa8"],
                ["#049c00", "#e8ff00", "#ff9a00", "#ff0000"]
            ],
        chooseText: "Select"
    });

const events =
{
    init
};

registerEvents("colorPicker", events);

function init(element: JQuery<HTMLElement>)
{
    const colorPicker = $(element);
    const settings = getControlSettings(colorPicker);
    colorPicker.spectrum(settings);
    addAccessibility(colorPicker, settings);
}

function addAccessibility(control: JQuery<HTMLElement>, settings: Spectrum.Options)
{
    const picker = control.next(".sp-skyward");
    if (picker.length === 0)
    {
        return;
    }

    // add tabindex so the picker is keyboard accessible
    picker.attr("tabindex", 0);

    const id = control.attr("id");
    let labelText: string | undefined | null;
    let label: JQuery<HTMLElement> | undefined;
    // make sure labels with `for` attributes still point to this control
    if (id)
    {
        const pickerId = `${id}_picker`;
        picker.attr("id", pickerId);

        const togglableColorPickerContainer = control.closest(".togglableColorPicker");
        if (togglableColorPickerContainer.length > 0)
        {
            const controlName = control.data("id");
            label = $.findLabel(controlName);

            const checkbox = togglableColorPickerContainer.find(".checkbox");
            const checkboxLabel = checkbox.findLabel();

            const checkboxLabelID = checkboxLabel.getIdAndPrefix().id;
            const labelIDAndPrefix = label.getIdAndPrefix();
            let labelID = labelIDAndPrefix.id;
            if (!labelID)
            {
                labelID = `${togglableColorPickerContainer.attr("id")}label`;
                label.attr("id", labelID);
            }

            checkbox.attr("aria-labelledby", `${labelID} ${checkboxLabelID}`);
        }
        else
        {
            label = control.findLabel();
            if (label.length === 0)
            {
                const controlName = control.getIdAndPrefix().id;
                if (controlName)
                {
                    label = $.findLabel(controlName);
                }
            }
        }

        if (label.length > 0)
        {
            label.removeAttr("for");
            labelText = label[0].textContent;
            label.on("click", () =>
            {
                picker.trigger("focus");
            })
        }
    }
    picker.attr("role", "combobox");
    picker.attr("aria-haspopup", "dialog");
    picker.attr("aria-expanded", "false");

    const selectedColor = control.spectrum("get");
    const ariaLabelText = getPickerAriaLabel(selectedColor, labelText);
    picker.attr("aria-label", ariaLabelText);

    const downArrow = picker.find(".sp-dd");
    downArrow.attr("aria-hidden", "true");

    picker.on("enter", function ()
    {
        picker.trigger("click");
    });

    const container = control.spectrum("container");

    container.on("enter", ".sp-thumb-el", function (this: HTMLElement)
    {
        const swatch = $(this);
        const hasDoneButton = container.find(".sp-choose:visible").length > 0;
        swatch.trigger("click");

        // If there is a done button the dialog will be rebuilt and the tabindexes will be lost.
        //call .show to ensure the tabindexes are re-built
        if (hasDoneButton && settings.show)
        {
            // @ts-ignore We don't need to pass in the color here
            settings.show();
        }
    });

    if (settings.disabled)
    {
        picker.attr("aria-readonly", "true");
        picker.attr("aria-describedby", readOnlyAnnouncementID);
    }
}

export function toggle(element: JQueryInput, isDisabled: boolean)
{
    const colorPicker = $(element);
    const picker = colorPicker.next(".sp-skyward");
    if (picker.length === 0)
    {
        return;
    }

    if (isDisabled)
    {
        colorPicker.spectrum("disable");
        picker.attr("aria-readonly", "true");
        picker.attr("aria-describedby", readOnlyAnnouncementID);
    }
    else
    {
        colorPicker.spectrum("enable");
        picker.removeAttr("aria-readonly");
        picker.removeAttr("aria-describedby");
    }
}

function getPickerAriaLabel(color: tinycolor.Instance, labelText?: string | null)
{
    let label = "Open color picker";
    if (labelText)
    {
        label = `${labelText}, ${label}`;
    }

    if (color)
    {
        label += `, selected color ${color.toHexString()}`
    }
    return label;
}

function getControlSettings(control: JQuery<HTMLElement>)
{
    let defaultSettings;

    if (control.hasClass("textColorPicker"))
    {
        defaultSettings = DEFAULT_PALETTE_ONLY_SETTINGS;
    }
    else if (control.hasClass("partialPaletteOnly"))
    {
        defaultSettings = DEFAULT_PARTIAL_PALETTE_ONLY_SETTINGS
    }
    else if (control.hasClass("teacherInputPaletteSettings"))
    {
        defaultSettings = DEFAULT_TEACHER_INPUT_PALETTE_SETTINGS;
    }
    else
    {
        defaultSettings = DEFAULT_INPUT_AND_PALETTE_SETTINGS;
    }

    const settings: Spectrum.Options = $.extend({}, defaultSettings);
    const controlData = control.data();

    if (controlData.colorPalette)
    {
        const colorPaletteArray = colorPaletteArrayAssembly(controlData.colorPalette);
        settings.palette = colorPaletteArray;
    }

    if (controlData.additionalColorPalette)
    {
        const colorPaletteArray = colorPaletteArrayAssembly(controlData.additionalColorPalette);
        if (settings.palette !== undefined)
        {
            settings.palette = settings.palette.concat(colorPaletteArray);
        }
    }

    settings.disabled = control.is("[readonly]");
    settings.allowEmpty = controlData.enableTransparent;

    if (controlData.color)
    {
        settings.color = controlData.color;
    }

    setSettingsCallbacks(control, settings);

    return settings;
}

function colorPaletteArrayAssembly(paletteArray: Array<any>)
{
    let partialArray: string[] = [];
    const fullArray: string[][] = [];

    for (let i = 0; i < paletteArray.length; i++)
    {
        if (i % 5 == 0)
        {
            partialArray = [];
            fullArray.push(partialArray);
        }

        partialArray.push(paletteArray[i]);
    }
    return fullArray
}

function setSettingsCallbacks(control: JQuery<HTMLElement>, settings: Spectrum.Options)
{
    let modalLayer: ModalLayer;
    let buttonsReplaced = false;

    settings.beforeShow = beforeShow;
    settings.show = show;
    settings.move = move;
    settings.change = change;
    settings.hide = hide;

    function beforeShow(_color: tinycolor.Instance)
    {
        const container = control.spectrum("container");

        if (!buttonsReplaced)
        {
            // We replace native buttons with our own to override styles and ordering
            replaceButtons(container);
            buttonsReplaced = true;
        }

        const beforeClose = function (layer: ModalLayer, reason?: string)
        {
            // cancel the change on escape
            if (reason === "escape")
            {
                container.find(".sp-cancel").trigger("click");
            }
            layer.container.find(container).detach();
        };

        const options =
        {
            closeOnClick: false,
            initialFocusElement: ".sp-thumb-active",
            preserveFocus: () => true
        };

        // We place the picker in a modal layer to prevent interaction outside of it
        modalLayer = newModalLayer(container, beforeClose, options);
        modalLayer.container.attr("tabindex", "-1");
    }

    function show(color: tinycolor.Instance)
    {
        const container = control.spectrum("container");

        if (!settings.showPaletteOnly)
        {
            // Clicking off of the color picker should not close
            $(control[0].ownerDocument).off("click.spectrum");
        }

        // @ts-ignore tinycolor is attached via spectrum
        const initialColor = tinycolor(color);

        // We replace the checkmark over transparent swatches to be more visible
        replaceTransparentSwatchCheckmark(container);

        addModalAccessibility(container);

        debounce(() =>
        {
            // @ts-ignore dialogFocusManager doesn't have a .d.ts file
            skyward.dialogFocusManager.setFocus(modalLayer);
        }, 1)();
    }

    function change(color: tinycolor.Instance)
    {
        const picker = control.next(".sp-skyward");
        if (picker.length === 0)
        {
            return;
        }

        const label = getPickerAriaLabel(color);
        picker.attr("aria-label", label);
    }

    function move(_color: tinycolor.Instance)
    {
        modalLayer.container.trigger("focus");

        const container = control.spectrum("container");
        addModalAccessibility(container);

        debounce(() =>
        {
            // @ts-ignore dialogFocusManager doesn't have a .d.ts file
            skyward.dialogFocusManager.setFocus(modalLayer);
        }, 1)();

        // We replace the checkmark over transparent swatches to be more visible
        replaceTransparentSwatchCheckmark(control.spectrum("container"));
    }

    function hide(_color: tinycolor.Instance)
    {
        if (modalLayer && !modalLayer.closing)
        {
            modalLayer.close();
        }

        const spectrumControlDiv = control.next(".sp-replacer");
        spectrumControlDiv.attr("aria-expanded", "false");
        spectrumControlDiv.removeAttr("aria-controls");
    }

    function addModalAccessibility(container: JQuery<HTMLElement>)
    {
        const spectrumControlDiv = control.next(".sp-replacer");
        spectrumControlDiv.attr("aria-expanded", "true");

        const dialogID = "colorPickerDialogContainer"
        container.attr("id", dialogID);
        container.removeAttr("tabindex");

        spectrumControlDiv.attr("aria-controls", dialogID);

        container.attr("aria-label", "Color Options");

        const paletteContainer = container.find(".sp-palette-container");
        paletteContainer.attr("aria-label", "Select a color");
        paletteContainer.attr("role", "listbox");

        const swatches = paletteContainer.find(".sp-thumb-el");
        swatches.each(function ()
        {
            const swatch = $(this);
            swatch.attr("tabindex", "0");
            swatch.attr("role", "option");

            const hexCode = swatch.attr("title")!;
            swatch.attr("aria-label", hexCode);

            let isSelected = false;
            if (swatch.hasClass("sp-thumb-active"))
            {
                isSelected = true;
            }

            swatch.attr("aria-selected", isSelected.toString());
        });

        const hexInput = container.find(".sp-input");
        if (hexInput.length > 0)
        {
            hexInput.attr("aria-label", "HEX Color Value");
        }
    }
}

function replaceButtons(container: JQuery<HTMLElement>)
{
    const buttonContainer = container.find(".sp-button-container");

    replaceButton(buttonContainer, ".sp-choose", "Save", "Save");
    replaceButton(buttonContainer, ".sp-cancel", "Cancel", "Cancel");
}

function replaceButton(buttonContainer: JQuery<HTMLElement>, selector: string, icon: string | Key, text: string)
{
    const button = buttonContainer.find(selector) as JQuery<HTMLButtonElement>;
    button.css("display", "none");
    buttonContainer.append(getReplacementButton(button, icon, text));
}

function getReplacementButton(button: JQuery<HTMLButtonElement>, icon: string | Key, text: string)
{
    const svg = getSvg(icon as Key).addClass("buttonSvg svgNoHover") as unknown as HTMLElement;
    const textSpan = $("<span>", { class: "sp-buttonText" }).html(text);

    const replacementButton = $("<a>", { role: "button", href: "javascript:void(0)", class: button.attr("class") + " sp-skyward-button" }).html(svg).append(textSpan);
    replacementButton.on("click", function () { button.trigger("click"); });

    return replacementButton;
}

function replaceTransparentSwatchCheckmark(container: JQuery<HTMLElement>)
{
    const activeSwatch = container.find(".sp-thumb-active");
    if (!activeSwatch.length)
    {
        return;
    }

    const color = activeSwatch.data("color");
    if (!color)
    {
        return;
    }

    // @ts-ignore tinycolor is attached via spectrum
    const isTransparent = tinycolor(color).getAlpha() === 0;
    if (!isTransparent)
    {
        return;
    }

    activeSwatch.removeClass("sp-thumb-dark");
    activeSwatch.addClass("sp-thumb-light");
}